import React from 'react';
import {
  formatGermanNumber,
  parseGermanNumberOnBlur,
  parseGermanNumberOrNull,
} from '../utils/parseGermanNumber';

type NativeInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'defaultValue' | 'onChange' | 'inputMode' | 'min' | 'max' | 'step'
>;

export type GermanDecimalInputProps = NativeInputProps & {
  value: number;
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

/**
 * Decimal field for German input ("12,75"). Text-based on purpose: while "12," is typed,
 * Safari reports an empty value for `type="number"`, and parsing per keystroke then
 * reset the field to 0 ("12,75" ended up as 75).
 */
export function GermanDecimalInput({
  value,
  onValueChange,
  min,
  max,
  minimumFractionDigits = 0,
  maximumFractionDigits = 6,
  onFocus,
  onBlur,
  ...rest
}: GermanDecimalInputProps) {
  const [draft, setDraft] = React.useState<string | null>(null);
  const editedRef = React.useRef(false);

  const format = (n: number) =>
    formatGermanNumber(n, { minimumFractionDigits, maximumFractionDigits });
  const inRange = (n: number) =>
    (min === undefined || n >= min) && (max === undefined || n <= max);

  return (
    <input
      {...rest}
      type="text"
      inputMode="decimal"
      value={draft ?? format(value)}
      onFocus={(e) => {
        editedRef.current = false;
        setDraft(format(value));
        onFocus?.(e);
      }}
      onChange={(e) => {
        const raw = e.target.value;
        editedRef.current = true;
        setDraft(raw);
        // Commit complete values right away so totals update while typing.
        const n = parseGermanNumberOrNull(raw);
        if (n !== null && inRange(n)) onValueChange(n);
      }}
      onBlur={(e) => {
        if (editedRef.current && draft !== null) {
          const n = parseGermanNumberOnBlur(draft);
          if (n !== null) {
            const clamped = Math.min(max ?? Infinity, Math.max(min ?? -Infinity, n));
            if (clamped !== value) onValueChange(clamped);
          }
        }
        setDraft(null);
        onBlur?.(e);
      }}
    />
  );
}
